/**
 * Build by chat (V1-E17-T03): a conversation with the copilot about the tenant's setup and calls. The copilot looks
 * things up as the user and drafts agents, changes, Call Scripts, campaigns, assist profiles and eval suites; each
 * draft is a card the user applies or discards. Nothing is written until they press Apply. What the copilot says is
 * shown as plain text: it may quote the tenant's calls, so nothing in it is rendered as markup or fetched.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, Loader2, MessageSquarePlus, Search, Send, Sparkles, Trash2, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { data, type Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { CopilotError } from "../agents/copilot/Copilot";
import { useFormat, useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";

type Chat = Schemas["ChatOut"];
type Message = Schemas["ChatMessageOut"];
type Draft = Schemas["ChatDraftOut"];
type Applied = Schemas["AppliedOut"];

const SUGGESTIONS: MessageId[] = ["chat.suggest.agent", "chat.suggest.calls", "chat.suggest.campaign", "chat.suggest.evals"];
const KIND: Record<Draft["kind"], MessageId> = {
  Agent: "chat.kind.agent", AgentChange: "chat.kind.change", ScriptApp: "chat.kind.script", Campaign: "chat.kind.campaign",
  AssistProfile: "chat.kind.assist", EvalSuite: "chat.kind.suite",
};

export function ChatPage() {
  const t = useT();
  const [open, setOpen] = useState<string | null>(null);
  const chats = useApiQuery("copilot:chats", (a) => a.GET("/v1/copilot/chats", { params: { query: { limit: 50 } } }));
  return (
    <Page title={t("chat.title")} description={t("chat.description")}
      actions={<Button variant="secondary" onClick={() => setOpen(null)}><MessageSquarePlus size={16} aria-hidden="true" />{t("chat.new")}</Button>}>
      <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
        <nav aria-label={t("chat.list")} className="space-y-2">
          <Section query={chats}>
            {(page) => page.data.length === 0
              ? <p className="text-sm text-[var(--color-muted)]">{t("chat.none")}</p>
              : (
                <ul className="space-y-1">
                  {page.data.map((c) => (
                    <li key={c.id}>
                      <button type="button" onClick={() => setOpen(c.id)} aria-current={open === c.id ? "true" : undefined}
                        className={`w-full truncate rounded-md px-2 py-1.5 text-left text-sm hover:bg-[var(--color-surface)] ${open === c.id ? "bg-[var(--color-surface)] font-medium" : ""}`}>
                        {c.title || t("chat.untitled")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
          </Section>
        </nav>
        {open ? <OpenChat key={open} id={open} onGone={() => setOpen(null)} /> : <NewChat onStarted={setOpen} />}
      </div>
    </Page>
  );
}

function NewChat({ onStarted }: { onStarted: (id: string) => void }) {
  const t = useT();
  const { api } = useBff();
  const queries = useQueryClient();
  const tenant = useMe().tenant_id;
  const start = useMutation({
    mutationFn: (text: string) => data(api.POST("/v1/copilot/chats", { body: { text } })),
    onSuccess: async (chat) => {
      queries.setQueryData([`copilot:chat:${chat.id}`, tenant], chat);
      await queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]) === "copilot:chats" });
      onStarted(chat.id);
    },
  });
  return (
    <section aria-label={t("chat.conversation")} className="space-y-4">
      <div className="rounded-lg border border-[var(--color-border)] p-4">
        <p className="flex items-center gap-2 font-medium"><Sparkles size={16} aria-hidden="true" />{t("chat.hello")}</p>
        <p className="mt-1 text-sm text-[var(--color-muted)]">{t("chat.helloNote")}</p>
        <ul aria-label={t("chat.suggestions")} className="mt-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <li key={s}>
              <Button variant="secondary" disabled={start.isPending} onClick={() => start.mutate(t(s))}>{t(s)}</Button>
            </li>
          ))}
        </ul>
      </div>
      <Composer pending={start.isPending} onSend={(text) => start.mutate(text)} />
      {start.isError && <CopilotError error={start.error} />}
    </section>
  );
}

function OpenChat({ id, onGone }: { id: string; onGone: () => void }) {
  const t = useT();
  const { api } = useBff();
  const queries = useQueryClient();
  const chat = useApiQuery(`copilot:chat:${id}`, (a) => a.GET("/v1/copilot/chats/{chat_id}", { params: { path: { chat_id: id } } }));
  const refresh = (next: Chat) => {
    queries.setQueriesData({ predicate: (q) => q.queryKey[0] === `copilot:chat:${id}` }, next);
    void queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]) === "copilot:chats" });
  };
  const say = useMutation({
    mutationFn: (text: string) => data(api.POST("/v1/copilot/chats/{chat_id}/messages", { params: { path: { chat_id: id } }, body: { text } })),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => data(api.DELETE("/v1/copilot/chats/{chat_id}", { params: { path: { chat_id: id } } })),
    onSuccess: async () => {
      await queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]) === "copilot:chats" });
      onGone();
    },
  });
  const end = useRef<HTMLDivElement>(null);
  const count = chat.data?.messages.length ?? 0;
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end" }); // a block: Chromium's returns a promise, which is no cleanup
  }, [count]);
  if (chat.isError) return <ErrorState error={chat.error} onRetry={() => void chat.refetch()} />;
  if (!chat.data) return <p role="status" aria-busy="true" className="text-sm text-[var(--color-muted)]">{t("chat.loading")}</p>;
  const c = chat.data;
  const drafts = new Map(c.drafts.map((d) => [d.id, d]));
  return (
    <section aria-label={t("chat.conversation")} className="min-w-0 space-y-4">
      <header className="flex items-center justify-between gap-2">
        <h2 className="truncate text-lg font-semibold">{c.title}</h2>
        <Button variant="ghost" onClick={() => remove.mutate()} disabled={remove.isPending} aria-label={t("chat.delete")}>
          <Trash2 size={16} aria-hidden="true" />
        </Button>
      </header>
      <ol aria-label={t("chat.messages")} className="space-y-4">
        {c.messages.map((m, i) => (
          <li key={i} aria-label={t(m.role === "user" ? "chat.you" : "chat.copilot")}
            className={m.role === "user" ? "ml-auto max-w-[85%] rounded-lg bg-[var(--color-surface)] px-3 py-2" : "space-y-3"}>
            <Said message={m} />
            {m.drafts.map((d) => drafts.get(d)).filter((d): d is Draft => Boolean(d)).map((d) => (
              <DraftCard key={d.id} chatId={id} draft={d} onChanged={refresh} />
            ))}
          </li>
        ))}
      </ol>
      <div ref={end} />
      {say.isPending && <p role="status" className="flex items-center gap-2 text-sm text-[var(--color-muted)]"><Loader2 size={16} aria-hidden="true" className="animate-spin" />{t("chat.thinking")}</p>}
      {say.isError && <CopilotError error={say.error} />}
      <Composer pending={say.isPending} onSend={(text) => say.mutate(text)} />
      <p className="text-xs text-[var(--color-muted)]">
        {t("chat.usage", { tokens: c.usage.input_tokens + c.usage.output_tokens, model: c.model ?? "—" })}
      </p>
    </section>
  );
}

function Said({ message }: { message: Message }) {
  const t = useT();
  const format = useFormat();
  const refused = message.lookups.filter((l) => !l.ok);
  return (
    <div className="space-y-2">
      <p className="whitespace-pre-wrap text-sm">{message.text}</p>
      {message.questions.length > 0 && (
        <ul aria-label={t("chat.questions")} className="list-disc space-y-1 pl-5 text-sm">
          {message.questions.map((q, i) => <li key={i}>{q}</li>)}
        </ul>
      )}
      {message.lookups.length > 0 && (
        <details className="text-xs text-[var(--color-muted)]">
          <summary className="cursor-pointer"><Search size={12} aria-hidden="true" className="mr-1 inline" />
            {t("chat.lookedUp", { count: message.lookups.length, refused: refused.length })}</summary>
          <ul className="mt-1 space-y-0.5 pl-4">
            {message.lookups.map((l, i) => (
              <li key={i}><code>{l.tool}</code>{l.ok ? "" : ` — ${l.error ?? t("chat.refused")}`}</li>
            ))}
          </ul>
        </details>
      )}
      <p className="sr-only">{format.dateTime(message.at)}</p>
    </div>
  );
}

function Composer({ pending, onSend }: { pending: boolean; onSend: (text: string) => void }) {
  const t = useT();
  const id = useId();
  const canWrite = useCan("agents:write");
  const [text, setText] = useState("");
  if (!canWrite) return <p className="text-sm text-[var(--color-muted)]">{t("chat.cannotTalk")}</p>;
  const send = (e?: FormEvent) => {
    e?.preventDefault();
    if (!text.trim() || pending) return;
    onSend(text.trim());
    setText("");
  };
  const keys = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) send(e);
  };
  return (
    <form onSubmit={send} className="space-y-2">
      <label htmlFor={id} className="sr-only">{t("chat.message")}</label>
      <textarea id={id} rows={3} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={keys} maxLength={8000}
        placeholder={t("chat.placeholder")} aria-describedby={`${id}-hint`}
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm" />
      <div className="flex items-center justify-between gap-2">
        <p id={`${id}-hint`} className="text-xs text-[var(--color-muted)]">{t("chat.hint")}</p>
        <Button type="submit" disabled={!text.trim() || pending}>
          {pending ? <Loader2 size={16} aria-hidden="true" className="animate-spin" /> : <Send size={16} aria-hidden="true" />}
          {t("chat.send")}
        </Button>
      </div>
    </form>
  );
}

function DraftCard({ chatId, draft, onChanged }: { chatId: string; draft: Draft; onChanged: (c: Chat) => void }) {
  const t = useT();
  const { api } = useBff();
  const queries = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);
  const path = { chat_id: chatId, draft_id: draft.id };
  const apply = useMutation({
    mutationFn: () => data(api.POST("/v1/copilot/chats/{chat_id}/drafts/{draft_id}:apply", { params: { path } })),
    onSuccess: async (made: Applied) => {
      setSecret(made.secret ?? null);
      onChanged(made.chat);
      await queries.invalidateQueries({ predicate: (q) => /^(agents|campaigns|script-apps|scripts|assist)/.test(String(q.queryKey[0])) });
    },
  });
  const discard = useMutation({
    mutationFn: () => data(api.POST("/v1/copilot/chats/{chat_id}/drafts/{draft_id}:discard", { params: { path } })),
    onSuccess: onChanged,
  });
  const status = draft.status === "applied" ? "chat.applied" : draft.status === "discarded" ? "chat.discarded" : draft.valid ? "chat.ready" : "chat.invalid";
  return (
    <article aria-label={draft.summary} className="space-y-3 rounded-lg border border-[var(--color-border)] p-4">
      <header className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-[var(--color-surface)] px-2 py-0.5 text-xs font-medium">{t(KIND[draft.kind])}</span>
        <h3 className="font-semibold">{draft.summary}</h3>
        <span className={`ml-auto text-xs ${draft.valid || draft.status !== "proposed" ? "text-[var(--color-muted)]" : "text-[var(--color-danger)]"}`}>{t(status)}</span>
      </header>
      <Preview draft={draft} />
      {draft.status === "proposed" && draft.errors.length > 0 && (
        <ul aria-label={t("chat.problems")} className="space-y-1 text-sm text-[var(--color-danger)]">
          {draft.errors.map((e, i) => <li key={i}>{e.path && <code className="text-xs">{e.path}</code>} {e.message}</li>)}
        </ul>
      )}
      {draft.status === "proposed" && draft.notices.length > 0 && (
        <ul aria-label={t("chat.notices")} className="space-y-1 text-sm text-[var(--color-muted)]">
          {draft.notices.map((n, i) => <li key={i}>{String(n.message ?? "")}</li>)}
        </ul>
      )}
      {draft.status === "proposed" && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => apply.mutate()} disabled={!draft.valid || apply.isPending || discard.isPending}>
            {apply.isPending ? <Loader2 size={16} aria-hidden="true" className="animate-spin" /> : <Check size={16} aria-hidden="true" />}
            {t("chat.apply")}
          </Button>
          <Button variant="secondary" onClick={() => discard.mutate()} disabled={apply.isPending || discard.isPending}>
            <X size={16} aria-hidden="true" />{t("chat.discard")}
          </Button>
        </div>
      )}
      {draft.status === "proposed" && <p className="text-xs text-[var(--color-muted)]">{t(draft.kind === "Campaign" ? "chat.campaignNote" : "chat.applyNote")}</p>}
      {apply.isError && <ErrorState error={apply.error} />}
      {discard.isError && <ErrorState error={discard.error} />}
      {draft.result && <Made result={draft.result} />}
      {secret && (
        <div role="status" className="space-y-1 rounded-md border border-[var(--color-border)] p-3 text-sm">
          <p className="font-medium">{t("chat.secret")}</p>
          <code className="block break-all text-xs">{secret}</code>
        </div>
      )}
    </article>
  );
}

function Preview({ draft }: { draft: Draft }) {
  const t = useT();
  const doc = (draft.document ?? {}) as { metadata?: { name?: string }; name?: string; spec?: { instructions?: string; greeting?: { text?: string } } };
  if (draft.kind === "AgentChange") {
    return (
      <ul aria-label={t("chat.changes")} className="space-y-1 text-sm">
        {(draft.changes ?? []).map((c, i) => (
          <li key={i}><code className="text-xs">{String(c.op)} {String(c.path)}</code>
            {"value" in c && <span className="block whitespace-pre-wrap text-[var(--color-muted)]">{typeof c.value === "string" ? c.value : JSON.stringify(c.value)}</span>}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="space-y-2 text-sm">
      {(doc.metadata?.name ?? doc.name) && <p><span className="text-[var(--color-muted)]">{t("chat.name")}</span> <code>{doc.metadata?.name ?? doc.name}</code></p>}
      {draft.kind === "Agent" && doc.spec?.greeting?.text && <p>“{doc.spec.greeting.text}”</p>}
      {draft.kind === "Agent" && doc.spec?.instructions && <p className="line-clamp-4 whitespace-pre-wrap">{doc.spec.instructions}</p>}
      <details>
        <summary className="cursor-pointer text-xs text-[var(--color-muted)]">{t("chat.document")}</summary>
        <pre className="mt-1 max-h-72 overflow-auto rounded bg-[var(--color-surface)] p-2 text-xs">{JSON.stringify(draft.document, null, 2)}</pre>
      </details>
    </div>
  );
}

function Made({ result }: { result: NonNullable<Draft["result"]> }) {
  const t = useT();
  const link = "text-[var(--color-primary)] underline";
  if ((result.kind === "Agent" || result.kind === "AgentChange") && result.id) {
    return <Link to="/agents/$agentId" params={{ agentId: result.id }} className={link}>{t(result.version && result.version > 1 ? "chat.openVersion" : "chat.openAgent", { version: result.version ?? 1 })}</Link>;
  }
  if (result.kind === "EvalSuite" && result.agent_id) {
    return <Link to="/agents/$agentId" params={{ agentId: result.agent_id }} search={{ tab: "evals" }} className={link}>{t("chat.openSuite")}</Link>;
  }
  if (result.kind === "Campaign" && result.id) {
    return <Link to="/campaigns/$campaignId" params={{ campaignId: result.id }} className={link}>{t("chat.openCampaign")}</Link>;
  }
  if (result.kind === "ScriptApp" && result.id) {
    return <Link to="/scripts/$appId" params={{ appId: result.id }} className={link}>{t("chat.openScript")}</Link>;
  }
  return <Link to="/assist" className={link}>{t("chat.openAssist")}</Link>;
}
